import { useEffect, useState } from 'react';
import { isDialApiConfigured } from '../api/client';
import { EMPTY_DASHBOARD, getDashboard, type DashboardData } from '../api/dashboard';

const POLL_MS = 5_000;

export function DashboardPage() {
  const [data, setData] = useState<DashboardData>(EMPTY_DASHBOARD);
  const [period, setPeriod] = useState<'month' | 'year'>('month');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const d = await getDashboard(period);
        if (!cancelled) {
          setData(d);
          setError(null);
        }
      } catch (err: unknown) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'load failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void fetchOnce();
    if (!isDialApiConfigured) return;
    const id = setInterval(() => void fetchOnce(), POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [period]);

  return (
    <main className="px-8 py-10">
      <div className="mx-auto max-w-6xl">
        <header className="flex items-baseline justify-between">
          <div>
            <h1 className="font-sans text-3xl font-semibold tracking-tight text-graphite-900">
              Dashboard
            </h1>
            <p className="mt-2 font-sans text-sm leading-relaxed text-graphite-700">
              Live, polled every {POLL_MS / 1000}s. Telesales / VM&E / Courtesy only.
            </p>
          </div>
          <p className="font-mono text-[0.7rem] text-graphite-500">
            {loading
              ? 'loading…'
              : `updated ${new Date(data.generated_at).toLocaleTimeString('en-AU')}`}
            {error ? <span className="ml-2 text-signal-amber">· {error}</span> : null}
          </p>
        </header>

        {!isDialApiConfigured ? (
          <p className="mt-6 rounded-md border border-dashed border-graphite-300 bg-surface-sunk px-4 py-2 font-mono text-xs text-graphite-500">
            Worker not reachable — tiles render zeros until the worker is wired.
          </p>
        ) : null}

        <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <Tile title="Telesales today">
            <Big number={data.telesales_today.total} suffix="calls" />
            <Leaders rows={data.telesales_today.leaders} />
          </Tile>

          <Tile title="Telesales this year">
            <Big number={data.telesales_year.total} suffix="calls" />
            <p className="mt-2 font-mono text-xs text-graphite-500">
              vs {data.telesales_year.last_year} last year ·{' '}
              {data.telesales_year.yoy_pct === null ? (
                <span>no YoY baseline</span>
              ) : (
                <span
                  className={
                    data.telesales_year.yoy_pct >= 0 ? 'text-graphite-900' : 'text-signal-amber'
                  }
                >
                  {data.telesales_year.yoy_pct >= 0 ? '+' : ''}
                  {data.telesales_year.yoy_pct}% YoY
                </span>
              )}
            </p>
          </Tile>

          <Tile
            title="Activities by owner"
            extra={
              <Toggle
                value={period}
                options={[
                  { id: 'month', label: 'Month' },
                  { id: 'year', label: 'Year' },
                ]}
                onChange={setPeriod}
              />
            }
          >
            <Leaders rows={data.by_owner.rows} />
          </Tile>

          <Tile title="Activities by purpose">
            <Pillbar
              segments={[
                { label: 'Telesales', value: data.by_purpose.telesales },
                { label: 'Courtesy', value: data.by_purpose.courtesy_call },
                { label: 'VM&E', value: data.by_purpose.vme },
              ]}
            />
          </Tile>

          <Tile title="Active sprints right now">
            <Big number={data.active_sprints.sprint_count} suffix="sprints" />
            <p className="mt-2 font-mono text-xs text-graphite-500">
              {data.active_sprints.contacts_being_worked} contact
              {data.active_sprints.contacts_being_worked === 1 ? '' : 's'} being worked
            </p>
          </Tile>

          <Tile title="Connect rate by user">
            <ConnectRateList rows={data.connect_rate} />
          </Tile>

          <Tile title={`Database updates this ${data.database_updates.period}`} wide>
            <DatabaseUpdatesList rows={data.database_updates.rows} />
          </Tile>
        </div>
      </div>
    </main>
  );
}

function Tile({
  title,
  extra,
  wide,
  children,
}: {
  title: string;
  extra?: React.ReactNode;
  wide?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section
      className={
        'rounded-lg border border-graphite-100 bg-surface p-5 ' +
        (wide ? 'md:col-span-2 xl:col-span-3' : '')
      }
    >
      <div className="flex items-baseline justify-between">
        <h3 className="font-mono text-[0.7rem] uppercase tracking-[0.18em] text-graphite-500">
          {title}
        </h3>
        {extra}
      </div>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function Big({ number, suffix }: { number: number; suffix?: string }) {
  return (
    <p className="flex items-baseline gap-2">
      <span className="font-sans text-4xl font-semibold tracking-tight text-graphite-900">
        {number.toLocaleString('en-AU')}
      </span>
      {suffix ? <span className="font-mono text-xs text-graphite-500">{suffix}</span> : null}
    </p>
  );
}

function Leaders({ rows }: { rows: readonly { label: string; count: number }[] }) {
  if (rows.length === 0) {
    return <p className="font-mono text-xs text-graphite-500">— no data yet —</p>;
  }
  const max = Math.max(...rows.map((r) => r.count), 1);
  return (
    <ul className="mt-2 space-y-1.5">
      {rows.map((r) => (
        <li key={r.label}>
          <div className="flex items-baseline justify-between font-mono text-xs">
            <span className="truncate text-graphite-900">{r.label}</span>
            <span className="text-graphite-500">{r.count}</span>
          </div>
          <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-surface-sunk">
            <div
              className="h-full bg-graphite-900"
              style={{ width: `${(r.count / max) * 100}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

function Pillbar({ segments }: { segments: readonly { label: string; value: number }[] }) {
  const total = segments.reduce((s, x) => s + x.value, 0);
  if (total === 0) {
    return <p className="font-mono text-xs text-graphite-500">— no data yet —</p>;
  }
  const colors = ['bg-graphite-900', 'bg-graphite-500', 'bg-signal-amber'];
  return (
    <div className="space-y-2">
      <div className="flex h-3 w-full overflow-hidden rounded-full bg-surface-sunk">
        {segments.map((s, i) => (
          <div
            key={s.label}
            className={colors[i] ?? 'bg-graphite-300'}
            style={{ width: `${(s.value / total) * 100}%` }}
            title={`${s.label}: ${s.value}`}
          />
        ))}
      </div>
      <ul className="grid grid-cols-3 gap-2 font-mono text-[0.7rem]">
        {segments.map((s, i) => (
          <li key={s.label} className="flex items-baseline gap-2">
            <span
              className={`size-1.5 rounded-full ${colors[i] ?? 'bg-graphite-300'}`}
              aria-hidden
            />
            <span className="text-graphite-500">{s.label}</span>
            <span className="ml-auto text-graphite-900">{s.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ConnectRateList({
  rows,
}: {
  rows: readonly { label: string; rate_pct: number; conversations: number; dials: number }[];
}) {
  if (rows.length === 0) {
    return <p className="font-mono text-xs text-graphite-500">— no data yet —</p>;
  }
  return (
    <ul className="space-y-1.5">
      {rows.map((r) => (
        <li key={r.label} className="flex items-baseline justify-between font-mono text-xs">
          <span className="truncate text-graphite-900">{r.label}</span>
          <span className="text-graphite-500">
            <span className={r.rate_pct >= 30 ? 'text-signal-amber' : ''}>{r.rate_pct}%</span>{' '}
            <span className="opacity-60">
              ({r.conversations}/{r.dials})
            </span>
          </span>
        </li>
      ))}
    </ul>
  );
}

function DatabaseUpdatesList({
  rows,
}: {
  rows: readonly { user_id: string; label: string; count: number }[];
}) {
  if (rows.length === 0) {
    return (
      <p className="font-mono text-xs text-graphite-500">
        No database updates logged yet this period.
      </p>
    );
  }
  return (
    <ul className="grid grid-cols-1 gap-1 sm:grid-cols-2 lg:grid-cols-3">
      {rows.map((r, i) => (
        <li key={r.user_id} className="flex items-baseline justify-between gap-2 font-mono text-xs">
          <span className="truncate text-graphite-900">
            <span className="text-graphite-500">{i + 1}.</span> {r.label}
          </span>
          <span className="text-graphite-700 tabular-nums">{r.count}</span>
        </li>
      ))}
    </ul>
  );
}

function Toggle<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: readonly { id: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex gap-1 rounded-md border border-graphite-300 bg-surface p-0.5">
      {options.map((opt) => (
        <button
          key={opt.id}
          type="button"
          onClick={() => onChange(opt.id)}
          className={
            'rounded px-2 py-0.5 font-mono text-[0.65rem] transition-colors ' +
            (opt.id === value ? 'bg-graphite-900 text-surface' : 'text-graphite-700')
          }
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}
